import { Box } from '@zoopla-eng/zds-components/box';
import { AuthModalMode, AuthModalProps } from '@zoopla/authentication/modal';
import { useAuthModal } from '@zoopla/authentication/use-auth-modal';
import { ArticleCollection } from '@zoopla/cms/article-collection';
import { CampaignCtaGql } from '@zoopla/landing-pages/campaign-cta';
import { FullwidthHeroLayout } from '@zoopla/layout/page';
import { SearchSuggestionsTownsAndCities } from '@zoopla/listing-search/suggested-towns-and-cities';
import { SavedListingsWidget } from '@zoopla/saved-listings-widget';
import { usePageTracking } from '@zoopla/tracking';
import dynamic from 'next/dynamic';
import * as React from 'react';
import { useState } from 'react';
import { contentMapper } from '../component-order-mapper/component-order-mapper';
import { HomepageHead } from '../components';
import { HomepageRegistrationFooter } from '../components/HomepageRegistrationFooter/HomepageRegistrationFooter';
import { TabbedSearch } from '../components/TabbedSearch/TabbedSearch';
import { ContentfulImageWrapper } from '../queries/heroContent.types';
import { WidgetWrapper } from './Homepage.styles';
import { Banner as BannerGql } from '../../graphql.generated';

export type HomepageProps = {
    brandHeadline: string;
    h1TitleTag: string;
    heroImage: ContentfulImageWrapper;
    isLoggedIn: boolean;
    content?: ArticleCollection[];
    contentBlock?: (ArticleCollection | CampaignCtaGql | BannerGql)[];
};

// TODO: move this back to being dynamically imported here
// but wrapped in Suspense, when we go with React 18
type DynamicOrStaticImportedComponents = {
    personalisedBanner: React.ReactElement;
};

const DynamicAuthModal = dynamic<AuthModalProps>(
    () =>
        import('@zoopla/authentication/modal').then(
            (module) => module.AuthModal
        ),
    { ssr: false }
);

export const Homepage = (
    props: HomepageProps & DynamicOrStaticImportedComponents
) => {
    const { personalisedBanner, isLoggedIn } = props;

    const { brandHeadline, h1TitleTag, heroImage, contentBlock } = props;
    const [isAuthModalShowing, openAuthModal, closeAuthModal] = useAuthModal();
    const [authMode, setAuthMode] = useState<AuthModalMode>('sign-in');

    usePageTracking('/homepage/', {
        section: 'homepage'
    });

    const handleOpenAuthModal = (
        triggerOrigin: React.RefObject<HTMLElement> | null,
        mode: AuthModalMode
    ) => {
        setAuthMode(mode);
        openAuthModal(triggerOrigin);
    };

    return (
        <>
            <HomepageHead />
            <FullwidthHeroLayout
                heroBackgroundColor={
                    isLoggedIn ? '$graphicErrorPageDay' : 'transparent'
                }
                heroContent={
                    <TabbedSearch
                        brandHeadline={brandHeadline}
                        image={heroImage}
                        h1TitleTag={h1TitleTag}
                        isLoggedIn={isLoggedIn}
                    />
                }
                content={
                    <>
                        {isLoggedIn && (
                            <Box
                                pt="$M100"
                                pb="$M100"
                                bpL100={{ pt: '$M300', pb: '$L200' }}
                                gridColumn="span 12"
                            >
                                {personalisedBanner}
                            </Box>
                        )}
                        <Box
                            fw
                            display="flex"
                            flexDirection="column"
                            rowGap="$M300"
                            bpM100={{ rowGap: '$L200' }}
                        >
                            <SavedListingsWidget />

                            {contentBlock && contentMapper(contentBlock)}

                            {!isLoggedIn && <SearchSuggestionsTownsAndCities />}

                            <WidgetWrapper>
                                <HomepageRegistrationFooter
                                    openAuthModal={handleOpenAuthModal}
                                />
                            </WidgetWrapper>
                        </Box>
                    </>
                }
            />
            <DynamicAuthModal
                heading="Get more from Zoopla"
                initialMode={authMode}
                isShowing={isAuthModalShowing}
                close={closeAuthModal}
                actionAfterAuth={{
                    action: () => null,
                    eventLabel: 'homepage',
                    segment: {
                        page: '/homepage/',
                        section: 'homepage'
                    }
                }}
            />
        </>
    );
};
